<template><div><h2 id="overview" tabindex="-1" class="d-docsite--header-2">Overview <a class="header-anchor d-link d-docsite--link d-link" href="#overview" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Data visualization is crucial for clear communication, but inconsistent color usage can hinder comprehension and create visual noise.
<strong>Chart Color Tokens</strong> provide a unified, robust, and accessible system for coloring data visualizations  across all Dialpad products.</p>
<div class="d-bgc-secondary d-bar-400 d-mbe-200">
 <svg-loader name="chart-header" />
</div>
<p class="d-docsite--paragraph">These tokens are designed to:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Ensure Visual Consistency:</strong> Guarantee a cohesive look and feel for data insights.</li>
<li class="d-docsite--list-element"><strong>Enhance Accessibility:</strong> Provide high-contrast, color-blind friendly palettes out-of-the-box.</li>
<li class="d-docsite--list-element"><strong>Streamline Workflow:</strong> Offer a single source of truth for both designers and developers, reducing guesswork and speeding up development.</li>
<li class="d-docsite--list-element"><strong>Improve Maintainability:</strong> Simplify global color updates and future-proof our data experiences.</li>
</ul>
<h2 id="types" tabindex="-1" class="d-docsite--header-2">Types <a class="header-anchor d-link d-docsite--link d-link" href="#types" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Chart Colors are available for one of four types: <a href="#single-color" class="d-docsite--link d-link">Single Color</a>, <a href="#semantic" class="d-docsite--link d-link">Semantic</a>, <a href="#categorical" class="d-docsite--link d-link">Categorical</a>, and <a href="#sequential" class="d-docsite--link d-link">Sequential</a>.</p>
<h3 id="single-color" tabindex="-1" class="d-docsite--header-3">Single Color <a class="header-anchor d-link d-docsite--link d-link" href="#single-color" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use for data visualizations that only require comparing one or two data points, or a single data point against a series of adjacent neutral data. Use <code v-pre>chart.color.accent</code> as the default. To bring a data point to focus, use <code v-pre>chart.color.accent</code> for the focused data point and <code v-pre>chart.color.neutral</code> for the rest.</p>
<div class="d-bgc-secondary d-bar-400 d-mbe-200">
  <svg-loader name="chart-singlecolor" />
</div>
<p class="d-docsite--paragraph"><strong>When to Use:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Basic data points.</li>
<li class="d-docsite--list-element">Indicating selected chart series or segments.</li>
<li class="d-docsite--list-element">Dimming or disabling inactive chart elements.</li>
</ul>
<token-table :tokens="singleColorTokens" :mode="resolvedMode" :show-value="false" /><h3 id="semantic" tabindex="-1" class="d-docsite--header-3">Semantic <a class="header-anchor d-link d-docsite--link d-link" href="#semantic" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Apply colors that associate meaning to the data points, such as status, severity, or sentiment.</p>
<div class="d-bgc-secondary d-bar-400 d-mbe-200">
  <svg-loader name="chart-semantic" />
</div>
<p class="d-docsite--paragraph"><strong>When to Use:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Performance Dashboards: Showing status (on-track, at-risk, complete).</li>
<li class="d-docsite--list-element">Alerts/Notifications: Highlighting critical data points.</li>
<li class="d-docsite--list-element">Sentiment Analysis: Representing positive, neutral, or negative sentiment.</li>
<li class="d-docsite--list-element">Brand Alignment: Emphasizing data related directly to Dialpad's core identity.</li>
</ul>
<token-table :tokens="semanticTokens" :mode="resolvedMode" :show-value="false" /><h3 id="categorical" tabindex="-1" class="d-docsite--header-3">Categorical <a class="header-anchor d-link d-docsite--link d-link" href="#categorical" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Apply unique colors to distinguish two or more unrelated data where color carries no meaning. Use in the predetermined numerical order, e.g. <code v-pre>01</code>, <code v-pre>02</code>, etc. This ensures applied data can be visually distinguished from its adjacent data.</p>
<div class="d-bgc-secondary d-bar-400 d-mbe-200">
  <svg-loader name="chart-categorical" />
</div>
<p class="d-docsite--paragraph"><strong>When to Use:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Multi-series bar charts, pie charts, or line charts comparing different groups.</li>
<li class="d-docsite--list-element">Visualizing discrete variables (e.g., customer segments, operating systems).</li>
</ul>
<token-table :tokens="categoricalTokens" :mode="resolvedMode" :show-value="false" /><h3 id="sequential" tabindex="-1" class="d-docsite--header-3">Sequential <a class="header-anchor d-link d-docsite--link d-link" href="#sequential" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">To represent data using progressive shades or tints of a single color, emphasizing their relative depth within a single data series.</p>
<div class="d-bgc-secondary d-bar-400 d-mbe-200">
  <svg-loader name="chart-sequential" />
</div>
<p class="d-docsite--paragraph"><strong>When to Use:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Single-series charts where visual depth or intensity within one category is key.</li>
<li class="d-docsite--list-element">Heatmaps or density visualizations for a single metric.</li>
<li class="d-docsite--list-element">Situations requiring a strong visual connection to the Dialpad brand.</li>
</ul>
<h4 class="d-docsite--header-4">Sequential Range</h4>
<p class="d-docsite--paragraph">Using sequential range tokens enable the ability to dynamically create varying number of color steps for a single data series with <code v-pre>start</code> and <code v-pre>end</code> tokens.</p>
<token-table :tokens="sequentialRangeTokens" :mode="resolvedMode" :show-value="false" /><h4 class="d-docsite--header-4">Sequential Default</h4>
<p class="d-docsite--paragraph">The default range are a fixed number of 10 available colors for a single data series. If you need to create fewer or more colors, use the sequential range tokens.</p>
<token-table :tokens="sequentialTokens" :mode="resolvedMode" :show-value="false" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<h3 id="developers" tabindex="-1" class="d-docsite--header-3">Developers <a class="header-anchor d-link d-docsite--link d-link" href="#developers" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Apply to chart elements (bars, lines, fills) via CSS Variables (e.g. <code v-pre>var(--dp-color-chart-color-accent)</code>) or corresponding CSS Utilities, e.g. <code v-pre>d-bgc-chart-neutral</code>, <code v-pre>d-bc-chart-warning</code>, etc.</p>
<h3 id="designers" tabindex="-1" class="d-docsite--header-3">Designers <a class="header-anchor d-link d-docsite--link d-link" href="#designers" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Applying Colors:</strong> Apply color directly to chart elements (bars, lines, fills) via Figma Styles linked to tokens. Unlike other Dialtone colors, Chart Colors are not scoped to foreground, surface, border, etc.</li>
<li class="d-docsite--list-element"><strong>Understanding Hover/Selected:</strong> Remember that hover and selected states for specific chart series (single color, semantic, categorical, and sequential) have predetermined contrasting colors, ensuring consistent interaction feedback.</li>
<li class="d-docsite--list-element"><strong>Prototyping:</strong> Use these tokens in your Figma prototypes to demonstrate interactive states and theme switching.</li>
</ul>
<h2 id="do-and-don-ts" tabindex="-1" class="d-docsite--header-2">Do and Don'ts <a class="header-anchor d-link d-docsite--link d-link" href="#do-and-don-ts" aria-hidden="true">#</a></h2>
<h3 id="use-chart-colors-only-for-data" tabindex="-1" class="d-docsite--header-3">Use chart colors only for data <a class="header-anchor d-link d-docsite--link d-link" href="#use-chart-colors-only-for-data" aria-hidden="true">#</a></h3>
<dialtone-usage>
  <template #do>
    Use chart colors for the data itself, not for other purposes.
  </template>
  <template #dont>
    Do not use chart colors for non-data purposes, e.g. supporting text labels, grid lines, etc. Use Dialtone's semantic colors for those purposes, e.g. "foreground primary", "border subtle", "background secondary", etc.
  </template>
</dialtone-usage>
<h3 id="apply-categorical-colors-in-numeric-order" tabindex="-1" class="d-docsite--header-3">Apply Categorical colors in numeric order <a class="header-anchor d-link d-docsite--link d-link" href="#apply-categorical-colors-in-numeric-order" aria-hidden="true">#</a></h3>
<dialtone-usage>
  <template #do>
    <svg-loader name="chart-dondont-1-1" />
    Use categorical and sequential colors in their predetermined order, e.g. 01, 02, 03, etc.
  </template>
  <template #dont>
    <svg-loader name="chart-dondont-1-2" />
    Avoid using categorical and sequential colors out of order, e.g. 10, 03, 05, etc.
  </template>
</dialtone-usage>
<h3 id="limit-chart-colors" tabindex="-1" class="d-docsite--header-3">Limit chart colors <a class="header-anchor d-link d-docsite--link d-link" href="#limit-chart-colors" aria-hidden="true">#</a></h3>
<dialtone-usage>
  <template #do>
    <svg-loader name="chart-dondont-2-1" />
    Where possible, limit the number of colors in a single graph to no more than 6.
  </template>
  <template #dont>
    <svg-loader name="chart-dondont-2-2" />
    Using more than 6 color may make the chart difficult to parse. Exhaust design possibilities before going beyond.
  </template>
</dialtone-usage>
<h3 id="use-multiple-colors-only-with-purpose" tabindex="-1" class="d-docsite--header-3">Use multiple colors only with purpose <a class="header-anchor d-link d-docsite--link d-link" href="#use-multiple-colors-only-with-purpose" aria-hidden="true">#</a></h3>
<dialtone-usage>
  <template #do>
    <svg-loader name="chart-dondont-3-1" />
    Use more than one color only when it serves the data's communication.
  </template>
  <template #dont>
    <svg-loader name="chart-dondont-3-2" />
    Using more than one color without purpose may make the chart difficult to parse and could confuse the user on the data's communication.
  </template>
</dialtone-usage>
<h3 id="use-space-to-separate-data" tabindex="-1" class="d-docsite--header-3">Use space to separate data <a class="header-anchor d-link d-docsite--link d-link" href="#use-space-to-separate-data" aria-hidden="true">#</a></h3>
<dialtone-usage>
  <template #do>
    <svg-loader name="chart-dondont-4-1" />
    Use space to separate data and make it easier to understand to the user.
  </template>
  <template #dont>
    <svg-loader name="chart-dondont-4-2" />
    Avoid mixing the tokens next to each other as much as possible, so keep a space to separate data and more digestible for the user.
  </template>
</dialtone-usage>
<h2 id="accessibility-guidelines" tabindex="-1" class="d-docsite--header-2">Accessibility Guidelines <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility-guidelines" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Ensuring accessible data visualizations is a core tenet of Dialtone. When using these tokens:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Contrast Ratios:</strong> All token color combinations are designed to meet WCAG AA contrast standards (minimum 3:1 for graphics/UI components).</li>
<li class="d-docsite--list-element"><strong>Color-Blindness Compatibility:</strong> Palettes have been vetted for common forms of color blindness (e.g., using tools like Viz Palette).</li>
<li class="d-docsite--list-element"><strong>Redundancy is Key:</strong> Never rely solely on color to convey critical information. Always provide supplementary visual cues such as:
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Labels/Text: Directly annotating data points with values.</li>
<li class="d-docsite--list-element">Icons/Patterns: Using distinct icons or patterns for categories.</li>
<li class="d-docsite--list-element">Shapes/Stroke Styles: Varying shapes, line styles (dashed, dotted), or stroke widths.</li>
</ul>
</li>
<li class="d-docsite--list-element"><strong>Theming Impact:</strong> Token values are specifically defined for both light and dark modes to ensure optimal contrast and readability in any theme.</li>
</ul>
<h2 id="known-issues-troubleshooting" tabindex="-1" class="d-docsite--header-2">Known Issues &amp; Troubleshooting <a class="header-anchor d-link d-docsite--link d-link" href="#known-issues-troubleshooting" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">While this system aims for robustness, be aware of potential challenges during implementation and adoption:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Charting Library Overrides:</strong> Some charting libraries might aggressively override inline styles or custom properties, requiring careful mapping or deeper customization.</li>
<li class="d-docsite--list-element"><strong>Complex Color Logic:</strong> For very custom data-driven color calculations beyond simple references (e.g., dynamic gradients based on data range), you might still need to implement logic in code that consumes these Chart Color design tokens.</li>
<li class="d-docsite--list-element"><strong>Transitioning Existing Charts:</strong> Migrating older charts not using design tokens will require dedicated refactoring effort.</li>
</ul>
</div></template>


<script setup>
import tokensJson from '@dialpad/dialtone-tokens/dist/doc.json';
import { alphabeticalSorter } from '@utilities';
import { useThemeManager } from '@composables/useThemeManager';

const { resolvedMode } = useThemeManager();

/*
* Remove unwanted background-clip classes
*@TODO: Remove this once background-clip classes are refactored to d-bgclip https://dialpad.atlassian.net/browse/DLT-2439
*/
const surfaceColorsExclusionList = ['box', 'text'];
const textColorsExclusionList = ['critical',  'success',  'warning',  'info'];
const statusTextColorsExclusionList = [
  'current',
  'transparent',
  'unset',
  'neutral',
  'primary',
  'secondary',
  'tertiary',
  'placeholder',
  'disabled',
  'muted',
];
const theme = "dp-light";
const isSemantic = (token) => /(warning|positive|info|critical|brand)/.test(token);
const isCategorical = (token) => /(categorical)/.test(token);
const isSequential = (token) => /(sequential)/.test(token) && !/(range)/.test(token);
const isSequentialRange = (token) => /(sequential-range-\d+-(start|end))/.test(token);

const {
  semanticTokens,
  categoricalTokens,
  sequentialTokens,
  sequentialRangeTokens,
  singleColorTokens
} = Object.keys(tokensJson[theme]).sort(alphabeticalSorter).reduce((acc, curr) => {
    if (!curr.startsWith('color/chart')) return acc;

    const { name, value, description } = tokensJson[theme][curr]["css/variables"];

    const tokenCategory = isSemantic(name) ? 'semanticTokens' :
                          isCategorical(name) ? 'categoricalTokens' :
                          isSequentialRange(name) ? 'sequentialRangeTokens' :
                          isSequential(name) ? 'sequentialTokens' :
                          'singleColorTokens';
    acc[tokenCategory].push({ name, tokenValue: value, description, exampleValue: value, exampleName: name });

    return acc;
  }, {
    semanticTokens: [],
    categoricalTokens: [],
    sequentialTokens: [],
    sequentialRangeTokens: [],
    singleColorTokens: [],
  });

// Sort sequential range tokens so 'start' comes before 'end' for each range number
sequentialRangeTokens.sort((a, b) => {
  const aMatch = a.name.match(/range-(\d+)-(start|end)/);
  const bMatch = b.name.match(/range-(\d+)-(start|end)/);

  if (aMatch && bMatch) {
    const aNum = parseInt(aMatch[1]);
    const bNum = parseInt(bMatch[1]);

    if (aNum !== bNum) {
      return aNum - bNum;
    }

    // For same number, 'start' comes before 'end'
    return aMatch[2] === 'start' ? -1 : 1;
  }

  return 0;
});
</script>